import { useState } from 'react'
import { FileText, Film, Image as ImageIcon, ExternalLink, Trash2, Palette } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useGetContentAssetUrl, useRemoveContentAsset } from '../../hooks/backend/marketing'
import { writeErrorMessage } from '../../utils/writeError'
import { fileSize, shortDate, type ContentAsset } from '../../utils/marketing'

/**
 * The files attached to a content item or a design request.
 *
 * NO THUMBNAILS. The bucket is private, so showing a picture means holding a
 * signed URL for every file on the screen, and a board with forty items would
 * mint forty of them on every render. Each URL is also an hour-long grant, and
 * minting one per row means minting them for files nobody opened.
 *
 * So a file is a row with its name, its size and when it arrived, and the URL is
 * fetched on the click that actually needs it. What the row does say without any
 * network call is whether it is a picture, a video or a document, because the
 * view already answered that in SQL.
 */
export function AssetStrip({
  assets,
  canRemove,
  onChanged,
}: {
  assets: ContentAsset[]
  canRemove: boolean
  onChanged: () => void
}) {
  const getUrl = useGetContentAssetUrl()
  const remove = useRemoveContentAsset()
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)

  async function open(asset: ContentAsset) {
    setOpeningId(asset.id)
    try {
      const res = (await getUrl.trigger({ id: asset.id }, { skipCache: true }).result) as {
        url: string
      }
      // A new tab, not a navigation. The operator is in the middle of a review
      // and losing the screen to look at a file is how a review gets abandoned.
      window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error('Could not open that file', { description: writeErrorMessage(err) })
    } finally {
      setOpeningId(null)
    }
  }

  async function drop(asset: ContentAsset) {
    setRemovingId(asset.id)
    try {
      const res = (await remove.trigger({ id: asset.id }).result) as {
        removed: boolean
        objectLeftBehind?: string
      }
      if (res.objectLeftBehind) {
        // Said out loud rather than swallowed. The list is correct, and there is
        // an unreachable object in the bucket. Nobody has to act on it today, and
        // nobody should discover it in six months either.
        toast.warning('Removed from the list', {
          description:
            'The file was removed everywhere it is shown, but the stored copy could not be deleted. Nothing is broken; please mention it so it can be cleaned up.',
        })
      } else {
        toast.success('File removed')
      }
      onChanged()
    } catch (err) {
      toast.error('Could not remove that file', { description: writeErrorMessage(err) })
    } finally {
      setRemovingId(null)
    }
  }

  if (assets.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No files attached yet.
      </p>
    )
  }

  return (
    <ul className="space-y-1.5">
      {assets.map((a) => {
        const Icon = a.is_image ? ImageIcon : a.is_video ? Film : FileText
        const size = fileSize(a.size_bytes)
        return (
          <li
            key={a.id}
            className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-2"
          >
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-foreground">{a.filename}</p>
              <p className="truncate text-[11px] text-muted-foreground">
                v{a.version}
                {size ? `, ${size}` : ''}, added {shortDate(a.uploaded_at)}
              </p>
            </div>
            {/* A file that came from a design request says so. Otherwise the
                design queue looks like it produced nothing. */}
            {a.from_design_request && (
              <span
                title="Delivered against a design request, which still holds it too"
                className="shrink-0 text-muted-foreground"
              >
                <Palette className="h-3.5 w-3.5" />
              </span>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 shrink-0 gap-1 px-2 text-xs"
              disabled={openingId === a.id}
              onClick={() => void open(a)}
            >
              {openingId === a.id ? (
                <Spinner className="h-3 w-3" />
              ) : (
                <ExternalLink className="h-3 w-3" />
              )}
              Open
            </Button>
            {canRemove && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Remove ${a.filename}`}
                className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                disabled={removingId === a.id}
                onClick={() => void drop(a)}
              >
                {removingId === a.id ? (
                  <Spinner className="h-3 w-3" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
